
import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const Terms = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Terms of Service | Upfixe</title>
        <meta name="description" content="Read Upfixe's Terms of Service to understand the terms and conditions for using our Google Ads automation platform." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://upfixe.com/terms" />
      </Helmet>
      <Header />
      <div className="container mx-auto px-4 pt-32 pb-12 flex-grow">
        <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>
        
        <div className="prose max-w-none">
          <p className="text-sm text-muted-foreground mb-4">
            UPFIXE, UNIPESSOAL LDA ("Upfixe Ads")
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">1. Agreement to Terms</h2>
          <p>
            By accessing or using Upfixe Ads, you agree to be bound by these Terms of Service and all applicable laws and regulations of Portugal. If you do not agree with any of these terms, you are prohibited from using or accessing this site.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">2. Use License</h2>
          <p>
            Permission is granted to use Upfixe Ads for personal or business use, subject to the restrictions in these Terms of Service. This is the grant of a license, not a transfer of title.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">3. Fair Usage Policy</h2>
          <p>
            While Upfixe Ads provides unlimited access to campaigns and brand creation, this is subject to our fair usage policy. We reserve the right to suspend or terminate accounts that demonstrate excessive or abusive usage patterns that may negatively impact the service quality for other users. The determination of what constitutes excessive use is at our sole discretion.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">4. Google Ads Integration</h2>
          <p>
            Upfixe Ads integrates with Google Ads. You are responsible for ensuring your use of our service complies with Google's terms of service and policies. We are not responsible for any actions taken by Google regarding your advertising accounts.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">5. Google API Services User Data Policy</h2>
          <p className="font-medium">
            Upfixe Ads explicitly affirms that Google Workspace APIs are not used to develop, improve, or train generalized AI and/or ML models. Any data accessed through Google APIs is used solely for the purpose of providing the specific functionality related to Google Ads management within our platform, and strictly in accordance with Google's API Services User Data Policy.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">5a. MCP Server &amp; AI Client Use</h2>
          <p>
            Upfixe operates a remote Model Context Protocol (MCP) server that
            you may connect to third-party AI clients including, but not
            limited to, Anthropic Claude, OpenAI ChatGPT, and Perplexity.
            Your use of those clients is governed by their respective terms
            and is not under our control. By connecting Upfixe to an AI
            client you accept the following:
          </p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>
              <strong>You authorise actions taken via your AI client.</strong>{" "}
              Tool calls initiated through your authenticated MCP session —
              including drafts and, after your explicit confirmation in chat,
              publishing or pausing of Google Ads campaigns — are deemed
              authorised by you and binding on your Google Ads account.
            </li>
            <li>
              <strong>Confirmation is required for write actions.</strong> Our
              MCP server marks all write tools (e.g. publish, pause, modify)
              as destructive and will not execute them without an explicit
              confirmation step. You are responsible for reviewing the action
              before confirming.
            </li>
            <li>
              <strong>Ad spend is your responsibility.</strong> Upfixe is not
              liable for advertising spend, campaign outcomes, lost revenue,
              or third-party charges arising from campaigns drafted,
              published, paused, or modified through the MCP server,
              regardless of whether the underlying instruction originated
              from you or from your AI client acting on your prompt. You are
              advised to use the daily spend caps and account-level safeguards
              available in your Google Ads account.
            </li>
            <li>
              <strong>No warranty on AI output.</strong> Outputs from your AI
              client (campaign suggestions, copy, keyword lists) are
              generated by third-party language models and are provided
              without warranty. You are responsible for reviewing all output
              before publishing.
            </li>
            <li>
              <strong>Revocation.</strong> You may disconnect the MCP server
              from your AI client at any time, and revoke Upfixe's access to
              your Google Ads account at any time. Revocation does not undo
              actions already taken.
            </li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">6. Account Responsibilities</h2>
          <p>
            You are responsible for maintaining the confidentiality of your account information, including your password, and for all activity that occurs under your account. You must notify us immediately of any unauthorized use of your account.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">7. Account Termination</h2>
          <p>
            We reserve the right to terminate or suspend access to our service immediately, without prior notice or liability, for any reason whatsoever, including but not limited to a breach of the Terms or abuse of our fair usage policy.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">8. Modifications to Service</h2>
          <p>
            Upfixe Ads reserves the right to modify or discontinue, temporarily or permanently, the service with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the service.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">9. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of Portugal, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the Portuguese courts.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">10. Changes to Terms</h2>
          <p>
            Upfixe Ads reserves the right to update or change these Terms of Service at any time. We will provide notice of any changes by updating the "Last Updated" date at the top of these Terms of Service.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">Contact Us</h2>
          <p>
            If you have any questions about these Terms, please contact us at info@upfixe.com.
          </p>
          
          <p className="mt-8 text-sm text-muted-foreground">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Terms;
